import type { HttpClient, HttpResponse, HttpRequestOptions } from '../http/HttpClient';

/**
 * Abstract base class for API clients.
 * Each API (BFF, External API, etc.) should extend this class
 * to define its specific configuration and allowed endpoints.
 *
 * @template TEndpoint - Union type of allowed endpoint strings
 */
export abstract class ApiClient<TEndpoint extends string = string> {
  protected httpClient: HttpClient;
  protected defaultPathParams: Record<string, string>;

  constructor(httpClient: HttpClient, defaultPathParams: Record<string, string> = {}) {
    this.httpClient = httpClient;
    this.defaultPathParams = defaultPathParams;
  }

  /**
   * Set authentication token for all subsequent requests
   */
  setAuthToken(token: string): void {
    this.httpClient.setAuthToken(token);
  }

  /**
   * Clear authentication token from all subsequent requests
   */
  clearAuthToken(): void {
    this.httpClient.clearAuthToken();
  }

  /**
   * Normalize endpoint by removing leading slash
   */
  protected normalizeEndpoint(endpoint: TEndpoint): TEndpoint {
    return endpoint.startsWith('/') ? endpoint.slice(1) as TEndpoint : endpoint;
  }

  /**
   * Replace path parameters in endpoint with actual values.
   * The path parameters should be written in the format {paramName} in the endpoint string.
   */
  protected replacePathParams(endpoint: string, pathParams?: Record<string, string>): string {
    if (!pathParams) {
      return endpoint;
    }

    let processedEndpoint = endpoint;
    for (const [key, value] of Object.entries(pathParams)) {
      processedEndpoint = processedEndpoint.replace(`{${key}}`, value);
    }

    return processedEndpoint;
  }

  /**
   * Process endpoint by validating it and replacing path parameters.
   * Merges defaultPathParams with explicit pathParams, where explicit values take precedence.
   */
  private processEndpoint(endpoint: TEndpoint, pathParams?: Record<string, string>): string {
    const mergedPathParams = { ...this.defaultPathParams, ...pathParams };
    return this.replacePathParams(this.normalizeEndpoint(endpoint), mergedPathParams);
  }

  /**
   * Make a GET request to the API
   */
  get<T>(endpoint: TEndpoint, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    const processedEndpoint = this.processEndpoint(endpoint, options?.pathParams);
    return this.httpClient.get<T>(processedEndpoint, options);
  }

  /**
   * Make a POST request to the API
   */
  post<T, P = unknown>(endpoint: TEndpoint, body?: P, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    const processedEndpoint = this.processEndpoint(endpoint, options?.pathParams);
    return this.httpClient.post<T>(processedEndpoint, body, options);
  }

  /**
   * Make a PUT request to the API
   */
  put<T, P = unknown>(endpoint: TEndpoint, body?: P, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    const processedEndpoint = this.processEndpoint(endpoint, options?.pathParams);
    return this.httpClient.put<T>(processedEndpoint, body, options);
  }

  /**
   * Make a DELETE request to the API
   */
  delete<T>(endpoint: TEndpoint, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    const processedEndpoint = this.processEndpoint(endpoint, options?.pathParams);
    return this.httpClient.delete<T>(processedEndpoint, options);
  }

  /**
   * Make a PATCH request to the API
   */
  patch<T, P = unknown>(endpoint: TEndpoint, body?: P, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    const processedEndpoint = this.processEndpoint(endpoint, options?.pathParams);
    return this.httpClient.patch<T>(processedEndpoint, body, options);
  }

  /**
   * Get file/blob data from the API
   */
  getFile<T>(endpoint: TEndpoint, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    const processedEndpoint = this.processEndpoint(endpoint, options?.pathParams);
    return this.httpClient.get<T>(processedEndpoint, {
      ...options,
      responseType: 'blob',
    });
  }
}
